import React, { useEffect } from "react";
import {
  EMPLOYMENT_STATUS_OPTIONS,
  MARITAL_STATUS_OPTIONS,
  RELATIONSHIP_OPTIONS,
  TITLE_OPTIONS,
} from "../../constants/membersConstants";
import { formatNumericInput } from "../../utils/validationHelpers";
import {
  getBrokerageFlags,
  showMaritalStatus,
} from "../../utils/brokerageFields";

/* ─── small shared select ─────────────────────────────────────────────── */
const SelectField = ({ label, value, onChange, options, placeholder }) => (
  <div className="flex flex-col gap-1">
    <label className="text-[11px] font-semibold text-gray-400 uppercase tracking-widest">
      {label}
    </label>
    <div className="relative">
      <select
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className={`w-full appearance-none pl-3 pr-8 py-2.5 rounded-xl border text-sm font-medium bg-white transition-all
          focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-emerald-400
          ${value ? "border-gray-300 text-gray-800" : "border-gray-200 text-gray-400"}`}
      >
        <option value="">{placeholder}</option>
        {options.map((o) => (
          <option key={o} value={o}>{o}</option>
        ))}
      </select>
      <span className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400">
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </span>
    </div>
  </div>
);

/* ─── text input ──────────────────────────────────────────────────────── */
const TextField = ({ label, value, onChange, placeholder }) => (
  <div className="flex flex-col gap-1">
    <label className="text-[11px] font-semibold text-gray-400 uppercase tracking-widest">
      {label}
    </label>
    <input
      type="text"
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="px-3 py-2.5 rounded-xl border border-gray-200 bg-white text-sm font-medium text-gray-800
        placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-emerald-400 transition-all"
    />
  </div>
);

/* ─── role toggle ─────────────────────────────────────────────────────── */
const RoleToggle = ({ isPrimary, onChange }) => (
  <div className="flex flex-col gap-1">
    <label className="text-[11px] font-semibold text-gray-400 uppercase tracking-widest">
      Role
    </label>
    <div className="flex rounded-xl border border-gray-200 overflow-hidden bg-gray-50 p-0.5 gap-0.5">
      {[{ label: "Primary Owner", val: true }, { label: "Secondary Owner", val: false }].map(({ label, val }) => (
        <button
          key={label}
          type="button"
          onClick={() => onChange(val)}
          className={`flex-1 py-2 px-2 text-sm font-semibold rounded-lg transition-all ${
            isPrimary === val
              ? val
                ? "bg-emerald-600 text-white shadow-sm"
                : "bg-blue-600 text-white shadow-sm"
              : "text-gray-500 hover:text-gray-700"
          }`}
        >
          {label}
        </button>
      ))}
    </div>
  </div>
);

/* ─── employment pill grid ────────────────────────────────────────────── */
const EmploymentGrid = ({ memberId, currentStatus, onChange }) => (
  <div className="flex flex-col gap-2">
    <label className="text-[11px] font-semibold text-gray-400 uppercase tracking-widest">
      Employment Status
    </label>
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
      {EMPLOYMENT_STATUS_OPTIONS.map((s) => {
        const selected = currentStatus === s;
        return (
          <button
            key={s}
            type="button"
            onClick={() => onChange(memberId, s)}
            className={`py-2 px-3 rounded-xl border text-sm font-medium transition-all ${
              selected
                ? "border-indigo-400 bg-indigo-50 text-indigo-700 shadow-sm"
                : "border-gray-200 bg-white text-gray-500 hover:border-gray-300 hover:bg-gray-50"
            }`}
          >
            {s}
          </button>
        );
      })}
    </div>
  </div>
);

/* ═══════════════════════════════════════════════════════════════════════ */
const Step1GeneralInfo = ({ data, updateData, onNext }) => {
  const brokerageFlags = getBrokerageFlags(data);

  useEffect(() => {
    const updates = {};
    if (data.is_fee_standard === undefined || data.is_fee_standard === null)
      updates.is_fee_standard = true;
    if (Object.keys(updates).length) updateData(updates);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /* helpers */
  const getPI = (member) => {
    if (!member.personal_info) return {};
    if (typeof member.personal_info === "string") {
      try { return JSON.parse(member.personal_info); } catch { return {}; }
    }
    return member.personal_info;
  };

  const setPI = (memberId, field, value) =>
    updateData({
      members: data.members.map((m) =>
        m.id !== memberId ? m : {
          ...m,
          personal_info: {
            ...(typeof m.personal_info === "object" && m.personal_info ? m.personal_info : {}),
            [field]: value,
          },
        }
      ),
    });

  const setRole = (memberId, isPrimary) =>
    updateData({
      members: data.members.map((m) =>
        m.id === memberId
          ? { ...m, is_primary: isPrimary }
          : isPrimary ? { ...m, is_primary: false } : m
      ),
    });

  const setEmployment = (memberId, status) =>
    updateData({
      members: data.members.map((m) => {
        if (m.id !== memberId) return m;
        const ei = typeof m.employment_info === "object" && m.employment_info ? m.employment_info : {};
        return { ...m, employment_info: { ...ei, status } };
      }),
    });

  const primaryMember  = data.members?.find((m) => m.is_primary);
  const primaryName    = (() => {
    if (!primaryMember) return "Primary Owner";
    const pi = getPI(primaryMember);
    return `${pi.first_name || ""} ${pi.last_name || ""}`.trim() || "Primary Owner";
  })();

  return (
    <div className="max-w-3xl mx-auto pb-4">
      {/* ── Page header ─────────────────────────────────────────────── */}
      <div className="text-center mb-10">
        <h2 className="text-3xl font-extrabold text-gray-900 mb-3">General Information</h2>
        <p className="text-base text-gray-500 max-w-xl mx-auto">
          Help us with these initial questions. It takes just a few minutes — you can skip anything you're unsure about.
        </p>
      </div>

      <div className="space-y-6">
        {/* ── Card 1: Fee Structure ─────────────────────────────────── */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="flex items-center gap-3 px-6 py-4 border-b border-gray-50 bg-gray-50/60">
            <span className="w-7 h-7 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center text-xs font-bold">1</span>
            <h3 className="text-base font-bold text-gray-800">Fee Structure</h3>
          </div>

          <div className="px-6 py-5 space-y-4">
            <div className="flex flex-wrap items-center gap-4">
              <span className="text-sm font-medium text-gray-600">Is the fee structure standard?</span>
              <div className="flex rounded-xl border border-gray-200 overflow-hidden bg-gray-50 p-0.5 gap-0.5">
                {[{ label: "Yes", val: true }, { label: "No", val: false }].map(({ label, val }) => (
                  <button
                    key={label}
                    onClick={() => updateData({
                      is_fee_standard: val,
                      custom_fee_percentage: val ? null : (data.custom_fee_percentage || ""),
                    })}
                    className={`px-6 py-1.5 text-sm font-semibold rounded-lg transition-all ${
                      data.is_fee_standard === val
                        ? "bg-emerald-600 text-white shadow-sm"
                        : "text-gray-500 hover:text-gray-700"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {!data.is_fee_standard && (
              <div className="flex items-center gap-3 animate-fadeIn">
                <div className="flex flex-col gap-1">
                  <label className="text-[11px] font-semibold text-gray-400 uppercase tracking-widest">Custom Fee</label>
                  <div className="relative w-36">
                    <input
                      type="number"
                      step="0.01"
                      min={0}
                      max={100}
                      value={data.custom_fee_percentage || ""}
                      onKeyDown={(e) => {
                        if (["-", "+", "e", "E"].includes(e.key)) {
                          e.preventDefault();
                        }
                      }}
                      onChange={(e) => {
                        let val = formatNumericInput(e.target.value, true);
                        if (val.includes(".")) {
                          const [intPart, decPart] = val.split(".");
                          val = intPart + "." + decPart.slice(0, 2);
                        }
                        const n = parseFloat(val);
                        if (!isNaN(n) && n > 100) {
                          val = "100";
                        }
                        updateData({ custom_fee_percentage: val });
                      }}
                      className="w-full pl-3 pr-8 py-2.5 rounded-xl border border-gray-200 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-emerald-400"
                      placeholder="0.00"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm font-bold">%</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ── Card per member ──────────────────────────────────────── */}
        {data.members?.length > 0 ? data.members.map((member, memberIdx) => {
          const pi        = getPI(member);
          const isPrimary = member.is_primary === true;

          return (
            <div
              key={member.id}
              className={`bg-white rounded-2xl shadow-sm border overflow-hidden transition-all ${
                isPrimary ? "border-emerald-200" : "border-blue-100"
              }`}
            >
              {/* Card header */}
              <div className={`flex items-center justify-between px-6 py-4 border-b ${
                isPrimary
                  ? "bg-gradient-to-r from-emerald-50 to-white border-emerald-100"
                  : "bg-gradient-to-r from-blue-50 to-white border-blue-100"
              }`}>
                <div className="flex items-center gap-3">
                  <span className="w-7 h-7 rounded-full bg-gray-100 text-gray-500 flex items-center justify-center text-xs font-bold">
                    {memberIdx + 2}
                  </span>
                  <div>
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-0.5">
                      Member {memberIdx + 1}
                    </p>
                    <p className="text-base font-bold text-gray-800 leading-none">
                      {[pi.title, pi.first_name, pi.last_name].filter(Boolean).join(" ") || "—"}
                    </p>
                  </div>
                </div>
                <span className={`text-xs font-bold px-3 py-1 rounded-full ${
                  isPrimary
                    ? "bg-emerald-100 text-emerald-700"
                    : "bg-blue-100 text-blue-700"
                }`}>
                  {isPrimary ? "Primary Member" : "Secondary Member"}
                </span>
              </div>

              <div className="px-6 py-5 space-y-5">
                {/* Row 1 — Title · First Name · Last Name */}
                <div className="grid grid-cols-12 gap-3">
                  <div className="col-span-12 sm:col-span-3">
                    <SelectField
                      label="Title"
                      value={pi.title}
                      onChange={(v) => setPI(member.id, "title", v)}
                      options={TITLE_OPTIONS}
                      placeholder="Select"
                    />
                  </div>
                  <div className="col-span-12 sm:col-span-4">
                    <TextField
                      label="First Name"
                      value={pi.first_name}
                      onChange={(v) => setPI(member.id, "first_name", v)}
                      placeholder="First name"
                    />
                  </div>
                  <div className="col-span-12 sm:col-span-5">
                    <TextField
                      label="Last Name"
                      value={pi.last_name}
                      onChange={(v) => setPI(member.id, "last_name", v)}
                      placeholder="Last name"
                    />
                  </div>
                </div>

                {/* Row 2 — Role · Marital Status */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <RoleToggle
                    isPrimary={member.is_primary}
                    onChange={(val) => setRole(member.id, val)}
                  />
                  {showMaritalStatus(brokerageFlags) && (
                  <SelectField
                    label="Marital Status"
                    value={pi.marital_status}
                    onChange={(v) => setPI(member.id, "marital_status", v)}
                    options={MARITAL_STATUS_OPTIONS}
                    placeholder="Select status"
                  />
                  )}
                </div>

                {/* Row 3 — Related To (secondary only) */}
                {!isPrimary && (
                  <SelectField
                    label={`RELATED TO — RELATIONSHIP TO ${primaryName.toUpperCase()}`}
                    value={pi.relationship}
                    onChange={(v) => setPI(member.id, "relationship", v)}
                    options={RELATIONSHIP_OPTIONS}
                    placeholder="Select relationship"
                  />
                )}

                {/* Divider */}
                <div className="border-t border-gray-50 pt-1" />

                {/* Row 4 — Employment Status */}
                <EmploymentGrid
                  memberId={member.id}
                  currentStatus={member.employment_info?.status}
                  onChange={setEmployment}
                />
              </div>
            </div>
          );
        }) : (
          <p className="text-center text-gray-400 italic py-8">No members found.</p>
        )}
      </div>

      {/* ── Actions ───────────────────────────────────────────────── */}
      <div className="flex justify-end mt-8">
        <button
          onClick={onNext}
          className="btn-primary px-8 py-3 rounded-xl shadow-md hover:shadow-lg hover:scale-[1.02] active:scale-100 transition-all flex items-center gap-2 font-semibold"
        >
          Next: Household Setup
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M14 5l7 7m0 0l-7 7m7-7H3" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default Step1GeneralInfo;
